// 웹 경로 (R7b D13·D15). 화면 코드는 경로 글자를 직접 쓰지 않고 여기 함수를 쓴다.
// 웹 경로 이름은 `/platforms`이고 API는 `/api/projects`다(설계서 결정 2). API 경로는 `lib/api/paths.ts`에 있다.

/** 작품 탭 셋: 설정집(작품 루트) · 등록 · 이미지 */
export type WorkTab = "bible" | "register" | "images";
/** 플랫폼 탭 넷: 작품(플랫폼 루트) · 플랫폼 설정 · 필드 목록 · 플랫폼 문서 */
export type PlatformTab = "works" | "settings" | "fields" | "documents";

/** 이미지 생성 화면의 대상 (`ImageTargetDto`와 같은 뜻, D50) */
export type ImageTargetKey = `${"reference" | "asset" | "work_image"}:${string}`;
/** 등록 화면 칸의 주인: 작품, 캐릭터(`c-<characterId>`), 에셋(`a-<slotId>`) (D48) */
export type OwnerKey = "work" | `c-${string}` | `a-${string}`;
/** 등록 화면 칸에서 이미지 생성으로 왔을 때 돌아갈 칸. `from`은 필드 키다 */
export interface ImageReturn {
  from: string;
  owner: OwnerKey;
}

/** 설정 탭 구간 (D63). 순서가 탭 순서다 */
export const SETTINGS_TABS = [
  "models",
  "keys",
  "accounts",
  "usage",
  "knowledge",
  "muse",
  "media",
] as const;
export type SettingsTab = (typeof SETTINGS_TABS)[number];

const PLATFORM_TAB_SEGMENT: Readonly<Record<PlatformTab, string>> = {
  works: "",
  settings: "/settings",
  fields: "/fields",
  documents: "/documents",
};

const WORK_TAB_SEGMENT: Readonly<Record<WorkTab, string>> = {
  bible: "",
  register: "/register",
  images: "/images",
};

/** 플랫폼 목록 */
export const HOME_PATH = "/";

/** 로그인 화면 */
export const LOGIN_PATH = "/login";

/** `/platforms/:p` (작품 탭) 또는 `/platforms/:p/{settings,fields,documents}` */
export function platformPath(projectId: string, tab: PlatformTab = "works"): string {
  return `/platforms/${encodeURIComponent(projectId)}${PLATFORM_TAB_SEGMENT[tab]}`;
}

/** `/platforms/:p/works/:w` (설정집) · `…/register` · `…/images`. `settings: true`면 작품 설정 다이얼로그가 열린다 (D30) */
export function workPath(
  projectId: string,
  workId: string,
  tab: WorkTab = "bible",
  query?: { settings?: true },
): string {
  const base = `${platformPath(projectId)}/works/${encodeURIComponent(workId)}${WORK_TAB_SEGMENT[tab]}`;
  return query?.settings === true ? `${base}?settings=1` : base;
}

/** 이미지 생성(대상 하나): `…/images?target=<kind>:<id>[&from=<fieldKey>&owner=<ownerKey>]` (D50) */
export function imageTargetPath(
  projectId: string,
  workId: string,
  target: ImageTargetKey,
  back?: ImageReturn,
): string {
  const params = new URLSearchParams({ target });
  if (back !== undefined) {
    params.set("from", back.from);
    params.set("owner", back.owner);
  }
  return `${workPath(projectId, workId, "images")}?${params.toString()}`;
}

/** 등록 화면 칸 앵커 이름 (D48): `cell-<fieldKey>-<owner>` */
export function registerCellId(fieldKey: string, owner: OwnerKey): string {
  return `cell-${fieldKey}-${owner}`;
}

/** 등록 화면의 그 칸: `…/register#cell-<fieldKey>-<owner>` (D48·D61) */
export function registerCellPath(
  projectId: string,
  workId: string,
  fieldKey: string,
  owner: OwnerKey,
): string {
  return `${workPath(projectId, workId, "register")}#${encodeURIComponent(registerCellId(fieldKey, owner))}`;
}

/**
 * `/settings/<tab>`. `from`은 뒤로 `<`가 돌아갈 경로다 (D16). 설정 탭끼리 옮길 때도 그대로 붙인다.
 * `from`이 없거나 `/`면 붙이지 않는다(뒤로의 기본값이 `/`다)
 */
export function settingsPath(tab: string, from?: string): string {
  const base = `/settings/${encodeURIComponent(tab)}`;
  if (from === undefined || from === "" || from === HOME_PATH) return base;
  return `${base}?${new URLSearchParams({ from }).toString()}`;
}
